import { IAppConfigResponse } from 'store/app/app.state';
import flags from './flags';
import { IChatMessage } from 'store/messages/messages.state';
import { DEFAULT_LANGUAGE } from 'translations';
import Browser from '../types';

export const setCSSVar = (name: string, val: string) => {
    document.documentElement.style.setProperty(name, val);
};

export const addStyle = (styleString: string) => {
    const style = document.createElement('style');
    style.textContent = styleString;
    document.head.append(style);
}

export const setDisableWarningWhenLeaving = (disabled?: boolean) => {
    if (disabled) {
        window.addEventListener("beforeunload", function (e) {
            // the absence of a returnValue property on the event will guarantee the browser unload happens
            delete e["returnValue"];
        });
    } else {
        window.addEventListener("beforeunload", function (e) {
            // Cancel the event
            e.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
            // Chrome requires returnValue to be set
            e.returnValue = "";
        });
    }
};

export const closeIframe = () => {
    postMessageToParent('close-iframe', null)
};

interface IDebouncerArgs {
    onCall: () => void;
    debounceTimeMs?: number;
}
export const debouncer = ({ onCall, debounceTimeMs = 2000 }: IDebouncerArgs) => {
    let debounced = false;

    const callable = () => {
        if (debounced) {
            return;
        }

        onCall();
        debounced = true;
        setTimeout(() => {
            debounced = false;
        }, debounceTimeMs)
    }

    return callable;
};

interface ITimeoutManagerArgs {
    onCall: (agentName?: string) => void;
    onTimeout: () => void;
    timeoutMs?: number

}
export const timeoutManager = ({ onCall, onTimeout, timeoutMs = 5000 }: ITimeoutManagerArgs) => {
    let timeout: NodeJS.Timeout;

    const callable = (agentName?: string) => {
        clearTimeout(timeout);
        onCall(agentName);
        timeout = setTimeout(() => {
            onTimeout();
        }, timeoutMs);
    };

    return callable;
}

export const capitaliseString = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
}


export const getUrlParameters = () => {
    const params = new URLSearchParams(window.location.search);

    const company = params.get('company');
    const facia = params.get('facia') ?? company;
    const chatFlowId = params.get('chatFlowId') ?? params.get('botID')
    const language = params.get('language')

    if (!company) {
        throw new Error('Missing company from query string parameters');
    }

    if (!chatFlowId) {
        throw new Error('Missing chat flow id from query string parameters');
    }

    return {
        company,
        facia,
        chatFlowId,
        language
    };
};

export const postMessageToParent = (type: string, content: any) => {
    window.parent.postMessage({
        type,
        content
    }, '*')
}

export const postMessageToParentPromise = (getType: string, receiveType: string) => {
    return new Promise<IAppConfigResponse | null>(res => {
        const timeout = setTimeout(() => res(null), 2000);

        window.addEventListener('message', (message: MessageEvent) => {
            if (message.data.type === receiveType) {
                res(message.data.content);
                clearTimeout(timeout);
            }
        });

        postMessageToParent(getType,{});
    });
};

export const getOS = () => {
    const userAgent = navigator.userAgent,
        platform = navigator.platform,
        macOsPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'],
        windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'],
        iOsPlatforms = ['iPhone', 'iPad', 'iPod'];
    let os = null;

    if (macOsPlatforms.indexOf(platform) !== -1) {
        os = 'Mac';
    } else if (iOsPlatforms.indexOf(platform) !== -1) {
        os = 'iOS';
    } else if (windowsPlatforms.indexOf(platform) !== -1) {
        os = 'Win';
    } else if (/Android/.test(userAgent)) {
        os = 'Android';
    } else if (!os && /Linux/.test(platform)) {
        os = 'Linux';
    }

    return os;
};

export const checkNetworkConnection = () => {
    return !!window.navigator?.onLine
}


interface IGetFlagAndDemonymArgs {
    targetLanguage: string,
    sourceLanguage?: string
}
export const getFlagAndDemonym = ({ targetLanguage, sourceLanguage }: IGetFlagAndDemonymArgs): [string, string] => {

    const languageNames = new Intl.DisplayNames([sourceLanguage ?? DEFAULT_LANGUAGE], {
        type: 'language'
    });

    const demonym = languageNames.of(targetLanguage ?? DEFAULT_LANGUAGE) ?? "";

    const flag = (() => {
        // Windows doesn't support flag emojis (29/3/2023)
        if (navigator.userAgent.search('Windows') > -1) {
            return ''
        }

        if (!Object.keys(flags).includes(targetLanguage)) {
            console.warn(`flag for country ${targetLanguage} not found`);
            return '';
        }
        return flags[targetLanguage].emoji
    })();

    return [flag, capitaliseString(demonym)]
}


export const downloadFile = (data: string, fileName: string) => {
    const blob = new Blob([data], {
        type: 'text/plain',
    });
    const urlBlob = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = urlBlob;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(urlBlob);
};

export const getFormattedTime = (date: Date) =>
    `${date?.getHours() < 10 ? `0${date?.getHours()}` : date?.getHours()
    }:${date?.getMinutes() < 10 ? `0${date?.getMinutes()}` : date?.getMinutes()
    }`;

export const mergeDeep = (
    target: Record<string, any>,
    ...sources: Record<string, any>[]
): Record<string, any> => {
    const isObject = (item: any) => {
        return item && typeof item === 'object' && !Array.isArray(item);
    };

    if (!sources.length) return target;
    const source = sources.shift();

    if (isObject(target) && isObject(source)) {
        for (const key in source) {
            if (isObject(source[key])) {
                if (!target[key]) Object.assign(target, { [key]: {} });
                mergeDeep(target[key], source[key]);
            } else {
                Object.assign(target, { [key]: source[key] });
            }
        }
    }

    return mergeDeep(target, ...sources);
};

export const isBanner = (message: IChatMessage) => {
    return typeof message.content !== 'string' && (message.content?.type === "INFORMATION_BANNER" || message.content?.type === "WARNING_BANNER")
}

export const getBrowser = () => {
    const { userAgent } = navigator;

    if (userAgent.indexOf('OPR') !== -1) {
        return Browser.OPERA;
    } else if (userAgent.indexOf('Edg') !== -1) {
        return Browser.EDGE;
    } else if (userAgent.indexOf('Safari') !== -1) {
        return Browser.SAFARI;
    } else if (userAgent.indexOf('Firefox') !== -1) {
        return Browser.FIREFOX;
    }

    return Browser.CHROME;
};
